'use client';

import { useEffect, useRef } from 'react';
import type { InputHTMLAttributes, TextareaHTMLAttributes, Ref } from 'react';
import { cx } from './cx';

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & {
  /** Поле не заполнено/не прошло проверку: подсветка рамкой, текст ошибки даёт форма. */
  bad?: boolean;
  className?: string;
  ref?: Ref<HTMLInputElement>;
};

/** Однострочное поле ввода. Вид — `.k-inp` (родной список iOS, Э4.2: радиус 14, 44/52). */
export function KInput({ bad, className, ref, ...rest }: InputProps) {
  return (
    <input
      {...rest}
      ref={ref}
      aria-invalid={bad || undefined}
      className={cx('k k-ctl k-inp', bad && 'is-bad', className)}
    />
  );
}

type TextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> & {
  bad?: boolean;
  /** Растёт под текст вместо внутренней прокрутки. */
  autoGrow?: boolean;
  /** Предел роста в пикселях; дальше появляется прокрутка. */
  maxHeight?: number;
  className?: string;
  ref?: Ref<HTMLTextAreaElement>;
};

/** Многострочное поле. `autoGrow` — растёт под текст (как композер чата). */
export function KTextarea({
  bad,
  autoGrow = false,
  maxHeight = 320,
  className,
  ref,
  value,
  onChange,
  ...rest
}: TextareaProps) {
  const own = useRef<HTMLTextAreaElement | null>(null);

  // Высота пересчитывается и при вводе, и при внешней подмене значения (подстановка
  // шаблона, сброс формы) — иначе поле остаётся раздутым от прежнего текста.
  useEffect(() => {
    if (!autoGrow) return;
    const el = own.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [autoGrow, maxHeight, value]);

  return (
    <textarea
      {...rest}
      value={value}
      onChange={onChange}
      ref={(node) => {
        own.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as { current: HTMLTextAreaElement | null }).current = node;
      }}
      aria-invalid={bad || undefined}
      className={cx('k k-ctl k-inp k-area', bad && 'is-bad', className)}
    />
  );
}
